import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import api from '../../lib/api';
import type { Absence, Employee } from '../../types';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
  Input, Button, Select, SelectContent, SelectItem, SelectTrigger, SelectValue, Textarea,
} from '@werkstatt/ui';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '../../components/ui/form';

const schema = z.object({
  employee_id: z.string().min(1, 'Pflichtfeld'),
  start_date: z.string().min(1, 'Pflichtfeld'),
  end_date: z.string().min(1, 'Pflichtfeld'),
  absence_type: z.string().min(1, 'Pflichtfeld'),
  status: z.string().default('approved'),
  note: z.string().optional(),
});

type FormValues = z.infer<typeof schema>;

interface Props {
  open: boolean;
  onClose: () => void;
  absence?: Absence | null;
}

export function AbsenceDialog({ open, onClose, absence }: Props) {
  const queryClient = useQueryClient();

  const { data: employees = [] } = useQuery({
    queryKey: ['employees', true],
    queryFn: async () => {
      const res = await api.get<Employee[]>('/employees/?is_active=true');
      return res.data;
    },
  });

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      employee_id: '',
      start_date: '',
      end_date: '',
      absence_type: 'vacation',
      status: 'approved',
      note: '',
    },
  });

  useEffect(() => {
    if (absence) {
      form.reset({
        employee_id: absence.employee_id,
        start_date: absence.start_date,
        end_date: absence.end_date,
        absence_type: absence.absence_type,
        status: absence.status,
        note: absence.note || '',
      });
    } else {
      form.reset({ employee_id: '', start_date: '', end_date: '', absence_type: 'vacation', status: 'approved', note: '' });
    }
  }, [absence, open, form]);

  const mutation = useMutation({
    mutationFn: async (values: FormValues) => {
      if (absence) {
        return api.put(`/absences/${absence.id}`, values);
      }
      return api.post('/absences/', values);
    },
    onSuccess: () => {
      toast.success(absence ? 'Abwesenheit aktualisiert' : 'Abwesenheit erstellt');
      queryClient.invalidateQueries({ queryKey: ['absences'] });
      onClose();
    },
    onError: (err: unknown) => {
      const msg = (err as { response?: { data?: { detail?: string } } }).response?.data?.detail || 'Fehler beim Speichern';
      toast.error(msg);
    },
  });

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display">{absence ? 'Abwesenheit bearbeiten' : 'Abwesenheit erfassen'}</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit((v) => mutation.mutate(v))} className="space-y-4">
            <FormField control={form.control} name="employee_id" render={({ field }) => (
              <FormItem>
                <FormLabel>Mitarbeiter *</FormLabel>
                <Select onValueChange={field.onChange} value={field.value}>
                  <FormControl>
                    <SelectTrigger><SelectValue placeholder="Mitarbeiter wählen" /></SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {employees.map((e) => (
                      <SelectItem key={e.id} value={e.id}>{e.last_name}, {e.first_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )} />

            <div className="grid grid-cols-2 gap-4">
              <FormField control={form.control} name="start_date" render={({ field }) => (
                <FormItem>
                  <FormLabel>Von *</FormLabel>
                  <FormControl><Input type="date" {...field} /></FormControl>
                  <FormMessage />
                </FormItem>
              )} />
              <FormField control={form.control} name="end_date" render={({ field }) => (
                <FormItem>
                  <FormLabel>Bis *</FormLabel>
                  <FormControl><Input type="date" {...field} /></FormControl>
                  <FormMessage />
                </FormItem>
              )} />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <FormField control={form.control} name="absence_type" render={({ field }) => (
                <FormItem>
                  <FormLabel>Typ *</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="vacation">Urlaub</SelectItem>
                      <SelectItem value="sick">Krankheit</SelectItem>
                      <SelectItem value="special">Sonderurlaub</SelectItem>
                      <SelectItem value="other">Sonstiges</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )} />
              <FormField control={form.control} name="status" render={({ field }) => (
                <FormItem>
                  <FormLabel>Status</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="pending">Ausstehend</SelectItem>
                      <SelectItem value="approved">Genehmigt</SelectItem>
                      <SelectItem value="rejected">Abgelehnt</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )} />
            </div>

            <FormField control={form.control} name="note" render={({ field }) => (
              <FormItem>
                <FormLabel>Notiz</FormLabel>
                <FormControl><Textarea rows={2} {...field} /></FormControl>
                <FormMessage />
              </FormItem>
            )} />

            <DialogFooter>
              <Button type="button" variant="outline" onClick={onClose}>Abbrechen</Button>
              <Button type="submit" disabled={mutation.isPending}>
                {mutation.isPending ? 'Speichern...' : 'Speichern'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
